import { useContext } from 'react';
import { enqueueSnackbar } from 'notistack';
import { ScoringContext } from '@/contexts/scoring/context';
import {
  FixtureChecklistAddElementMsg,
  SCORING_WORKER_HOST_ACTION,
  FixtureChecklistRemoveElementMsg,
  AddPerfectScorerRatingMsg,
  RemoveScorerRatingMsg,
} from '@/workers/scoring/types';
import { ChecklistCheckboxState } from '@/contexts/checklist/types';
import { CHECKLIST_ELEMENT_INDEX } from '@/service/types/checklist';
import { useScoringRate } from '@/service/hooks/useScoringRate';
import { SUCCESS_STRING } from '@/constants/dictionary';
import { USER_ROLE_NAME } from '@/service/constants';

export interface UseChecklistCheckboxParams {
  checklistCheckboxState: ChecklistCheckboxState;
}

export function useChecklistCheckbox({
  checklistCheckboxState: checkbox,
}: UseChecklistCheckboxParams) {
  const {
    state: { fixtureId, fixtureChecklist, collectionId },
    useDispatchWithResponse,
  } = useContext(ScoringContext);
  const { comparisonRating, mutate } = useScoringRate();

  const checklistElement = fixtureChecklist
    ? fixtureChecklist.find(({ index }) => index === checkbox.index)
    : null;

  const slaChecklistElement =
    fixtureChecklist &&
    fixtureChecklist.find(
      ({ index }) => index === CHECKLIST_ELEMENT_INDEX.SLA_BREACH_COMPLETE,
    );

  const addChecklistElementAction =
    useDispatchWithResponse<FixtureChecklistAddElementMsg>(
      SCORING_WORKER_HOST_ACTION.CHECKLIST_ELEMENT_ADD,
    );

  const AddPerfectScorerRatingMsg =
    useDispatchWithResponse<AddPerfectScorerRatingMsg>(
      SCORING_WORKER_HOST_ACTION.ADD_PERFECT_SCORER_RATING,
    );

  const removeScorerRating = useDispatchWithResponse<RemoveScorerRatingMsg>(
    SCORING_WORKER_HOST_ACTION.REMOVE_SCORER_RATING,
  );

  const comparisonRatingFilter =
    comparisonRating?.filter(
      (rating) => rating.role.name === USER_ROLE_NAME.MAIN_SCORER,
    ) || [];

  const check = async () => {
    if (!checklistElement) return;
    addChecklistElementAction.dispatch({
      fixtureId,
      collectionId,
      elementId: checklistElement.elementId,
    });

    if (
      collectionId &&
      slaChecklistElement &&
      checklistElement.index === CHECKLIST_ELEMENT_INDEX.ALL_MATCH_DATA_CORRECT
    ) {
      AddPerfectScorerRatingMsg.dispatch({
        fixtureId,
        collectionId,
        requestTime: new Date().toISOString(),
      })
        .then(() => {
          addChecklistElementAction.dispatch({
            fixtureId,
            collectionId,
            elementId: slaChecklistElement.elementId,
          });
        })
        .then(() => {
          mutate();
        })
        .finally(() => {
          enqueueSnackbar(SUCCESS_STRING.SUCCESS_PERFECT_RATING, {
            variant: 'success',
          });
        });
    }
  };

  const removeChecklistElementAction =
    useDispatchWithResponse<FixtureChecklistRemoveElementMsg>(
      SCORING_WORKER_HOST_ACTION.CHECKLIST_ELEMENT_REMOVE,
    );

  const uncheck = async () => {
    if (!checklistElement) return;
    removeChecklistElementAction.dispatch({
      fixtureId,
      collectionId,
      elementId: checklistElement.elementId,
    });
    if (
      collectionId &&
      slaChecklistElement &&
      checklistElement.index ===
        CHECKLIST_ELEMENT_INDEX.ALL_MATCH_DATA_CORRECT &&
      comparisonRatingFilter.length
    ) {
      for (const rating of comparisonRatingFilter) {
        const defaultRequestData = {
          fixtureId,
          collectionId,
          requestTime: new Date().toISOString(),
          oktaId: rating?.user.oktaId,
        };

        await removeScorerRating.dispatch(defaultRequestData, {
          successMessage: SUCCESS_STRING.RATING_SUCCESSFULLY_REMOVED,
        });

        mutate();
      }

      removeChecklistElementAction.dispatch({
        fixtureId,
        collectionId,
        elementId: slaChecklistElement.elementId,
      });
    }
  };

  const isLoading =
    addChecklistElementAction.isLoading ||
    removeChecklistElementAction.isLoading;

  return {
    check,
    uncheck,
    isLoading,
  };
}
